import React from 'react';
import Layout from '../../components/Layout';
import Icon from '../../components/AppIcon';
import { Link } from 'react-router-dom';

const InvestorRelationsPage = () => {
  const keyMetrics = [
    { label: 'Platform GMV', value: '$28M', change: '+127% YoY' },
    { label: 'ARR', value: '$4.2M', change: '+89% YoY' },
    { label: 'Active Vendors', value: '12,400+', change: '+156% YoY' },
    { label: 'Vendor Retention', value: '94.2%', change: 'Industry leading' },
  ];

  const unitEconomics = [
    { metric: 'CAC', value: '$45', description: 'Customer acquisition cost' },
    { metric: 'LTV', value: '$890', description: 'Lifetime value per vendor' },
    { metric: 'LTV:CAC', value: '19.8x', description: 'Strong unit economics' },
    { metric: 'Payback', value: '2.1mo', description: 'CAC payback period' },
  ];

  const documents = [
    { name: 'Pitch Deck', description: 'Company overview and investment thesis', icon: 'Presentation', protected: true },
    { name: 'Corporate One-Pager', description: 'Quick summary for partners', icon: 'FileText', protected: false },
    { name: 'Financial Model', description: 'Projections and assumptions', icon: 'Calculator', protected: true },
    { name: 'TAM Analysis', description: 'Market size and opportunity', icon: 'PieChart', protected: false },
  ];

  const fundingInfo = {
    stage: 'Series A',
    raised: '$2.4M',
    structure: 'Delaware C-Corp',
    investors: ['Angel investors', 'Strategic partners', 'Institutional funds'],
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="py-20 bg-primary text-white">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <div className="max-w-3xl mb-12">
            <p className="text-sm font-medium text-white/60 uppercase tracking-wider mb-4">
              Investor Relations
            </p>
            <h1 className="text-hero mb-6">
              Financial Hub for Investors
            </h1>
            <p className="text-xl text-white/80">
              Comprehensive information for institutional investors, VCs, and strategic
              partners evaluating United Empresas' market opportunity.
            </p>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {keyMetrics.map((metric, index) => (
              <div key={index} className="bg-white/10 backdrop-blur rounded-brand p-6">
                <div className="text-3xl font-semibold mb-1">{metric.value}</div>
                <div className="text-sm text-white/60 mb-2">{metric.label}</div>
                <div className="text-xs text-accent-light">{metric.change}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Investment Thesis */}
      <section className="py-20 bg-white">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <h2 className="text-display text-foreground mb-6">
                Investment Thesis
              </h2>
              <p className="text-body-large mb-6">
                United Empresas is building the infrastructure layer for Latin America's
                $1.2 trillion service economy—a market that remains largely informal and undigitized.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <Icon name="Check" size={20} className="text-accent mt-0.5" />
                  <span className="text-foreground"><strong>Massive TAM:</strong> $1.2T service economy with 60%+ informal participation</span>
                </li>
                <li className="flex items-start gap-3">
                  <Icon name="Check" size={20} className="text-accent mt-0.5" />
                  <span className="text-foreground"><strong>First-mover advantage:</strong> No scaled competitor in the region</span>
                </li>
                <li className="flex items-start gap-3">
                  <Icon name="Check" size={20} className="text-accent mt-0.5" />
                  <span className="text-foreground"><strong>Strong unit economics:</strong> 19.8x LTV:CAC ratio</span>
                </li>
                <li className="flex items-start gap-3">
                  <Icon name="Check" size={20} className="text-accent mt-0.5" />
                  <span className="text-foreground"><strong>Multiple revenue streams:</strong> Subscriptions, take-rate, and financial services</span>
                </li>
              </ul>
            </div>

            <div className="bg-muted rounded-brand-lg p-8">
              <h3 className="text-lg font-semibold text-foreground mb-6">
                Funding & Structure
              </h3>
              <div className="space-y-4 mb-8">
                <div className="flex justify-between py-3 border-b border-border">
                  <span className="text-muted-foreground">Current Stage</span>
                  <span className="font-medium text-foreground">{fundingInfo.stage}</span>
                </div>
                <div className="flex justify-between py-3 border-b border-border">
                  <span className="text-muted-foreground">Capital Raised</span>
                  <span className="font-medium text-foreground">{fundingInfo.raised}</span>
                </div>
                <div className="flex justify-between py-3 border-b border-border">
                  <span className="text-muted-foreground">Corporate Structure</span>
                  <span className="font-medium text-foreground">{fundingInfo.structure}</span>
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-2">Investor Types</div>
                <div className="flex flex-wrap gap-2">
                  {fundingInfo.investors.map((investor, i) => (
                    <span key={i} className="px-3 py-1 bg-white text-sm rounded-full">
                      {investor}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Unit Economics */}
      <section className="py-20 bg-muted">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-display text-foreground mb-6">
              Unit Economics
            </h2>
            <p className="text-body-large">
              Strong fundamentals that demonstrate efficient growth and path to profitability.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {unitEconomics.map((item, index) => (
              <div key={index} className="bg-white rounded-brand p-6 text-center border border-border">
                <div className="text-3xl font-semibold text-primary mb-2">{item.value}</div>
                <div className="text-lg font-medium text-foreground mb-1">{item.metric}</div>
                <div className="text-sm text-muted-foreground">{item.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Market Opportunity */}
      <section className="py-20 bg-white">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <h2 className="text-display text-foreground mb-12">
            Market Opportunity
          </h2>

          <div className="grid lg:grid-cols-3 gap-8">
            <div className="bg-primary rounded-brand p-8 text-white">
              <div className="text-4xl font-semibold mb-2">$1.2T</div>
              <div className="text-lg font-medium mb-4">Total Addressable Market</div>
              <p className="text-white/70 text-sm">
                Latin America's service economy across beauty, wellness, home services,
                healthcare, education, and professional services.
              </p>
            </div>

            <div className="bg-muted rounded-brand p-8">
              <div className="text-4xl font-semibold text-foreground mb-2">$180B</div>
              <div className="text-lg font-medium text-foreground mb-4">Serviceable Addressable Market</div>
              <p className="text-muted-foreground text-sm">
                Target segments in our initial markets: Dominican Republic, Puerto Rico,
                Miami, New York, and planned expansions.
              </p>
            </div>

            <div className="bg-muted rounded-brand p-8">
              <div className="text-4xl font-semibold text-foreground mb-2">$12B</div>
              <div className="text-lg font-medium text-foreground mb-4">Serviceable Obtainable Market</div>
              <p className="text-muted-foreground text-sm">
                Realistic capture opportunity over the next 5 years based on
                expansion plans and market penetration assumptions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Documents */}
      <section className="py-20 bg-muted">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <div className="mb-12">
            <h2 className="text-display text-foreground mb-4">
              Investor Documents
            </h2>
            <p className="text-muted-foreground">
              Access materials to evaluate the investment opportunity.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {documents.map((doc, index) => (
              <div key={index} className="bg-white rounded-brand p-6 border border-border flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-brand-sm flex items-center justify-center">
                    <Icon name={doc.icon} size={24} className="text-primary" />
                  </div>
                  <div>
                    <div className="font-medium text-foreground">{doc.name}</div>
                    <div className="text-sm text-muted-foreground">{doc.description}</div>
                  </div>
                </div>
                <button className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary hover:bg-primary/5 rounded-brand-sm transition-colors">
                  {doc.protected ? (
                    <>
                      <Icon name="Lock" size={14} />
                      Request
                    </>
                  ) : (
                    <>
                      <Icon name="Download" size={14} />
                      Download
                    </>
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-primary">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-display text-white mb-6">
                Connect with our IR Team
              </h2>
              <p className="text-white/70 mb-8">
                Schedule a call with our investor relations team to discuss the opportunity,
                request access to protected documents, or explore strategic partnerships.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="mailto:investors@unitedempresaas.com"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-primary font-medium rounded-brand-sm hover:bg-white/90 transition-colors"
                >
                  <Icon name="Mail" size={16} />
                  investors@unitedempresaas.com
                </a>
                <Link
                  to="/partnerships"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-white/30 text-white font-medium rounded-brand-sm hover:bg-white/10 transition-colors"
                >
                  Partnership Opportunities
                </Link>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur rounded-brand p-8">
              <h3 className="text-lg font-semibold text-white mb-6">
                Quarterly Updates
              </h3>
              <p className="text-white/70 mb-6 text-sm">
                Subscribe to receive quarterly investor updates including:
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-2 text-sm text-white/80">
                  <Icon name="Check" size={14} className="text-accent-light" />
                  Financial performance reports
                </li>
                <li className="flex items-center gap-2 text-sm text-white/80">
                  <Icon name="Check" size={14} className="text-accent-light" />
                  Market expansion updates
                </li>
                <li className="flex items-center gap-2 text-sm text-white/80">
                  <Icon name="Check" size={14} className="text-accent-light" />
                  Product development roadmap
                </li>
                <li className="flex items-center gap-2 text-sm text-white/80">
                  <Icon name="Check" size={14} className="text-accent-light" />
                  Key milestone announcements
                </li>
              </ul>
              <button className="w-full px-4 py-3 bg-white text-primary font-medium rounded-brand-sm hover:bg-white/90 transition-colors">
                Subscribe to Updates
              </button>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default InvestorRelationsPage;
